import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

export default class VariantPicker extends Component {
  #pendingRequestUrl;
  #abortController;
  #overrideInterval = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.variantChanged.bind(this));
    this.#startPriceGuard();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopPriceGuard();
  }

  #startPriceGuard() {
    this.#stopPriceGuard();
    const collectionPosId = document.querySelector('[data-collection-pos-id]')?.getAttribute('data-collection-pos-id');
    if (!collectionPosId) return;
    let ticks = 0;
    this.#overrideInterval = setInterval(() => {
      this.updateOverridePrice();
      if (++ticks >= 150) this.#stopPriceGuard(); // 150 × 100ms = 15 seconds
    }, 100);
  }

  #stopPriceGuard() {
    if (this.#overrideInterval !== null) {
      clearInterval(this.#overrideInterval);
      this.#overrideInterval = null;
    }
  }

  variantChanged(event) {
    this.#startPriceGuard(); // restart interval so corrections keep firing after each variant change
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    // Apply the category price override immediately — this variant's ID is now
    // reflected in this.selectedOption so we can price it before the network fetch
    // starts. A setTimeout(0) fires a second correction after any synchronous
    // change listeners on other scripts have run.
    this.updateOverridePrice();
    setTimeout(() => this.updateOverridePrice(), 0);

    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);
    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

 // Place this inside your VariantPicker class

// Inside your VariantPicker class
updateOverridePrice() {
  const pricesJson = document.getElementById('variant-override-prices');
  const prices = pricesJson ? JSON.parse(pricesJson.textContent) : {};
  const selectedOption = this.selectedOption;
  const variantId = selectedOption?.dataset.variantId || null;
  const collectionPosId = document
    .querySelector('[data-collection-pos-id]')
    ?.getAttribute('data-collection-pos-id');

  // Product container + price node
  const productId = this.closest('[data-product]')?.getAttribute('data-current-product-id');
  const productContainer = document.querySelector(`[data-product][data-current-product-id="${productId}"]`);
  const priceNode = productContainer?.querySelector('[data-product-price]');
  const hiddenPriceInput = productContainer?.querySelector('input[name="properties[_for_price_collection]"]');

  // Helper: get original variant price in cents
  function getOriginalVariantPriceCents(vid) {
    // 1) From a dataset on the selected DOM option (if you have it)
    const centsFromDataset = selectedOption?.dataset.priceCents || selectedOption?.dataset.price;
    if (centsFromDataset && !Number.isNaN(Number(centsFromDataset))) {
      // Many themes store cents in data-price; if it’s dollars, multiply below
      const n = Number(centsFromDataset);
      return n > 10000 ? n : Math.round(n); // assume already cents
    }

    // 2) From ShopifyAnalytics (commonly present)
    const saVariants = window.ShopifyAnalytics?.meta?.product?.variants;
    if (Array.isArray(saVariants)) {
      const v = saVariants.find(v => String(v.id) === String(vid));
      if (v?.price) {
        // ShopifyAnalytics stores price in cents as an integer
        return Number(v.price);
      }
    }

    // 3) From product JSON on the page (some themes expose this)
    const productJsonEl = productContainer?.querySelector('[data-product-json]');
    if (productJsonEl) {
      try {
        const productJson = JSON.parse(productJsonEl.textContent);
        const v = productJson?.variants?.find(v => String(v.id) === String(vid));
        if (v?.price) return Number(v.price); // price is usually in cents
      } catch (e) {}
    }

    // 4) Parse from the visible price text as a last resort (locale-agnostic-ish)
    if (priceNode?.textContent) {
      const normalized = priceNode.textContent
        .replace(/[^\d.,]/g, '')       // keep digits and separators
        .replace(/\s/g, '');
      // Heuristic: turn "1,234.56" or "1.234,56" into cents
      const comma = normalized.lastIndexOf(',');
      const dot = normalized.lastIndexOf('.');
      let dollars = normalized;
      if (comma > -1 && dot > -1) {
        dollars = dot > comma
          ? normalized.replace(/,/g, '')         // US: 1,234.56 -> 1234.56
          : normalized.replace(/\./g, '').replace(',', '.'); // EU: 1.234,56 -> 1234.56
      } else {
        // If only one separator and it appears within last 3 chars, treat as decimal
        const sep = comma > -1 ? ',' : (dot > -1 ? '.' : null);
        if (sep && normalized.length - (sep === ',' ? comma : dot) <= 3) {
          dollars = normalized.replace(sep, '.');
        } else {
          dollars = normalized.replace(/[.,]/g, ''); // integer dollars
        }
      }
      const num = Number(dollars);
      if (!Number.isNaN(num)) return Math.round(num * 100);
    }

    return null;
  }

  // 1) Try to build an override price (in cents)
  let overrideCents = null;

  if (
    variantId &&
    Array.isArray(prices[variantId]) &&
    prices[variantId].length &&
    collectionPosId
  ) {
    const priceObj = prices[variantId].find(item => String(item.category) === String(collectionPosId));
    if (priceObj && priceObj.price != null && priceObj.price !== '') {
      const asNumber = Number(priceObj.price);
      if (!Number.isNaN(asNumber)) {
        overrideCents = Math.round(asNumber * 100);
      }
    }
  }

  // 2) Fallback to original price if no override was found
  if (!Number.isInteger(overrideCents) && variantId) {
    const originalCents = getOriginalVariantPriceCents(variantId);
    if (Number.isInteger(originalCents)) {
      overrideCents = originalCents;
    }
  }

  // 3) Update UI
  if (priceNode && Number.isInteger(overrideCents)) {
    const formattedPrice = (window.Shopify && typeof Shopify.formatMoney === 'function')
      ? Shopify.formatMoney(overrideCents, window.Shopify.money_format)
      : `$${(overrideCents / 100).toFixed(2)}`;

    // Write our price into the native price element.
    const alreadyCorrect =
      priceNode.getAttribute('data-overridden') === 'true' &&
      priceNode.textContent.trim() === formattedPrice;

    if (!alreadyCorrect) {
      if (!priceNode.dataset.originalText) {
        priceNode.dataset.originalText = priceNode.textContent.trim();
      }
      priceNode.textContent = formattedPrice;
      priceNode.setAttribute('data-overridden', 'true');
    }
  } else if (priceNode && priceNode.dataset.originalText) {
    priceNode.textContent = priceNode.dataset.originalText;
    priceNode.removeAttribute('data-overridden');
  }

  // 4) Update hidden input (only if we have a valid number)
  if (hiddenPriceInput && Number.isInteger(overrideCents)) {
    hiddenPriceInput.value = (overrideCents / 100).toFixed(2);
  }
}

updateOverridePrices() {
  const pricesJson = document.getElementById('variant-override-prices');
  const prices = pricesJson ? JSON.parse(pricesJson.textContent) : {};
  const selectedOption = this.selectedOption;
  const variantId = selectedOption?.dataset.variantId || null;
  const collectionPosId = document.querySelector('[data-collection-pos-id]')?.getAttribute('data-collection-pos-id');

  // Get the current product ID from the closest data-product container
  const productId = this.closest('[data-product]')?.getAttribute('data-current-product-id');
  // Find the correct product container and price node
  const productContainer = document.querySelector(`[data-product][data-current-product-id="${productId}"]`);
  const priceNode = productContainer?.querySelector('[data-product-price]');



  let overrideCents = null;
  if (
    variantId &&
    Array.isArray(prices[variantId]) &&
    prices[variantId].length &&
    collectionPosId
  ) {
    const priceObj = prices[variantId].find(item => item.category == collectionPosId);
    if (priceObj && priceObj.price) {
      overrideCents = Math.round(parseFloat(priceObj.price) * 100);
    }
  }
 
  if (priceNode) {
    if (Number.isInteger(overrideCents)) {
      if (!priceNode.dataset.originalText) {
        priceNode.dataset.originalText = priceNode.textContent.trim();
      }
  
      priceNode.textContent = window.Shopify && Shopify.formatMoney
        ? Shopify.formatMoney(overrideCents, window.Shopify.money_format)
        : `$${(overrideCents / 100).toFixed(2)}`;
       
      priceNode.setAttribute('data-overridden', 'true');

    } else if (priceNode.dataset.originalText) {
      priceNode.textContent = priceNode.dataset.originalText;
      priceNode.removeAttribute('data-overridden');
        
    }
  }
    const hiddenPriceInput = productContainer?.querySelector('input[name="properties[_for_price_collection]"]');
     console.log(hiddenPriceInput.value)
    if (hiddenPriceInput) {
      hiddenPriceInput.value = (overrideCents / 100).toFixed(2);
    }

}
  // Patch the correct category price directly into fetched HTML before it is morphed
  // into the live DOM, preventing any flash of the wrong Shopify default price.
  #patchPriceInHtml(html, variantJsonText) {
    const collectionPosId = document.querySelector('[data-collection-pos-id]')?.getAttribute('data-collection-pos-id');
    if (!collectionPosId) return;

    // Get the variant ID that will become selected after the morph.
    // The JSON blob in the fetched HTML contains the new variant's data.
    let variantId = null;
    try {
      const variantData = JSON.parse(variantJsonText);
      variantId = String(variantData?.id ?? '');
    } catch (e) { return; }
    if (!variantId) return;

    // Look up the category price from the override prices JSON already in the live DOM.
    const pricesJson = document.getElementById('variant-override-prices');
    const prices = pricesJson ? JSON.parse(pricesJson.textContent) : {};
    const priceEntries = prices[variantId];
    if (!Array.isArray(priceEntries)) return;

    const priceObj = priceEntries.find(item => String(item.category) === String(collectionPosId));
    if (!priceObj || priceObj.price == null) return;

    const overrideCents = Math.round(Number(priceObj.price) * 100);
    if (!Number.isInteger(overrideCents)) return;

    const formattedPrice = (window.Shopify && typeof Shopify.formatMoney === 'function')
      ? Shopify.formatMoney(overrideCents, window.Shopify.money_format)
      : `$${(overrideCents / 100).toFixed(2)}`;

    // Patch the price node inside the fetched (off-screen) HTML document.
    const priceNode = html.querySelector('[data-product-price]');
    if (priceNode) {
      priceNode.textContent = formattedPrice;
      priceNode.setAttribute('data-overridden', 'true');
      priceNode.style.removeProperty('display');
    }
  }

  updateVariantPicker(newHtml) {
    let newProduct;
    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());
    if (!newVariantPickerSource) throw new Error('No new variant picker source found');

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;
      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }
      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    // --- Apply override price after morph ---
    this.updateOverridePrice();

    // --- Update native preorder state after morph ---
    try {
      const variantJson = this.querySelector('script[type="application/json"]');
      if (variantJson) {
        this.#updatePreorderState(JSON.parse(variantJson.textContent));
      }
    } catch (e) { /* ignore parse errors */ }

    return newProduct;
  }

  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');
    if (!main || !newMain) throw new Error('No new main source found');
    morph(main, newMain);

    // --- Apply override price after morph ---
    this.updateOverridePrice();
  }

  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);
      if (!targetElement) throw new Error('Target element not found');
      target = targetElement;
    }
    if (target instanceof HTMLInputElement) target.checked = true;
    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);
      if (!newSelectedOption) throw new Error('Option not found');
      for (const option of target.options) option.removeAttribute('selected');
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');
        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        // Pre-patch the fetched HTML with the correct category price BEFORE morphing
        // so the wrong Shopify default price never flashes in the live DOM.
        this.#patchPriceInHtml(html, textContent);

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * Toggles the native pre-order UI when the selected variant's inventory state changes.
   * Called after each variant morph. Reads inventory_policy / inventory_quantity from
   * the variant JSON blob (added to variant-main-picker.liquid) and:
   *   - Sets data-is-preorder on product-form-component (CSS shows/hides the info box)
   *   - Updates the add-to-cart button text
   *   - Enables/disables the preorder hidden cart-property inputs
   */
  #updatePreorderState(variantData) {
    const isPreorder =
      variantData.inventory_management === 'shopify' &&
      Number(variantData.inventory_quantity) <= 0 &&
      variantData.inventory_policy === 'continue';

    const productId = this.closest('[data-product]')?.getAttribute('data-current-product-id');
    const productContainer = document.querySelector(`[data-product][data-current-product-id="${productId}"]`);
    const formComponent = productContainer?.querySelector('product-form-component');
    if (!formComponent) return;

    formComponent.dataset.isPreorder = isPreorder ? 'true' : 'false';

    // Update button text
    const btnText = formComponent.querySelector('.add-to-cart-text__content');
    if (btnText) {
      btnText.textContent = isPreorder
        ? (formComponent.dataset.preorderText || 'Pre-order now')
        : (formComponent.dataset.addToCartText || 'Add to cart');
    }

    // Enable/disable hidden preorder cart-property inputs
    formComponent.querySelectorAll('[data-preorder-input]').forEach((input) => {
      input.disabled = !isPreorder;
    });
  }

  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');
    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }
    return selectedOption;
  }

  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;
    if (!optionValueId) throw new Error('No option value ID found');
    return optionValueId;
  }

  get selectedOptionsValues() {
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));
    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;
      if (!optionValueId) throw new Error('No option value ID found');
      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}